import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import WorkCard from "@/components/feature/WorkCard";
import Pagination from "@/components/base/Pagination";
import { categories } from "@/mocks/categories";
import { works } from "@/mocks/works";
import { useSeo } from "@/hooks/useSeo";
import type { Work } from "@/types";

const PAGE_SIZE = 9;

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") || "";

  const [input, setInput] = useState(query);
  const [sort, setSort] = useState<"latest" | "hot">("latest");
  const [year, setYear] = useState("all");
  const [page, setPage] = useState(1);

  useSeo({
    title: query
      ? `“${query}”搜索结果 - 女性ins大片`
      : "搜索作品 - 女性ins大片摄影作品检索",
    description: query
      ? `在女性ins大片中搜索“${query}”相关的女性摄影作品，涵盖时尚、艺术、生活、创意等风格。`
      : "搜索女性ins大片平台上的女性摄影作品，按关键词快速定位你感兴趣的时尚、艺术、生活与创意大片。",
    keywords: "搜索女性摄影作品,女性ins大片,摄影作品检索,时尚大片搜索",
    path: "/search",
  });

  const allWorks = works as Work[];

  const results = useMemo(() => {
    const kw = query.trim().toLowerCase();
    let list = allWorks.filter((w) => {
      if (!kw) return true;
      const haystack = `${w.title} ${w.author} ${w.categoryName} ${w.description} ${w.tags.join(" ")} ${w.location}`.toLowerCase();
      return haystack.includes(kw);
    });
    if (year !== "all") list = list.filter((w) => w.date.startsWith(year));
    if (sort === "latest") list = list.sort((a, b) => (a.date < b.date ? 1 : -1));
    if (sort === "hot") list = list.sort((a, b) => b.views - a.views);
    return list;
  }, [allWorks, query, year, sort]);

  const allYears = useMemo(() => {
    const set = new Set<string>();
    allWorks.forEach((w) => set.add(w.date.slice(0, 4)));
    return Array.from(set).sort((a, b) => (a < b ? 1 : -1));
  }, [allWorks]);

  const hotCategories = useMemo(
    () =>
      categories.map((c) => ({
        ...c,
        count: allWorks.filter((w) => w.categoryId === c.id).length,
      })),
    [allWorks]
  );

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = results.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    const next = new URLSearchParams(params);
    if (input.trim()) next.set("q", input.trim());
    else next.delete("q");
    setParams(next);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SiteNavbar />
      <main className="pt-16 md:pt-[72px]">
        <section className="bg-secondary-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-14">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4" aria-label="面包屑">
              <Link to="/" className="hover:text-primary-600 cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">搜索</span>
            </nav>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
              搜索作品
            </h1>
            <p className="text-sm text-foreground-600 mb-6">输入关键词，快速找到你感兴趣的时尚女性摄影作品</p>
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-2xl">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
                <input
                  type="text"
                  name="q"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="搜索作品标题、摄影师、风格或地点"
                  className="w-full h-12 pl-11 pr-4 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                />
              </div>
              <button
                type="submit"
                className="h-12 px-7 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </form>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <p className="text-sm text-foreground-600">
              {query ? (
                <>
                  关键词 <span className="text-primary-600 font-semibold">“{query}”</span> 找到{" "}
                  <span className="text-primary-600 font-semibold">{results.length}</span> 件作品
                </>
              ) : (
                <>
                  全部作品共 <span className="text-primary-600 font-semibold">{results.length}</span> 件
                </>
              )}
            </p>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500">年份</span>
                <select
                  value={year}
                  onChange={(e) => {
                    setYear(e.target.value);
                    setPage(1);
                  }}
                  className="h-9 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-700 focus:outline-none focus:border-primary-400 cursor-pointer"
                >
                  <option value="all">全部</option>
                  {allYears.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                {[
                  { key: "latest", label: "最新" },
                  { key: "hot", label: "最热" },
                ].map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => {
                      setSort(s.key as "latest" | "hot");
                      setPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                      sort === s.key
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {paged.length === 0 ? (
            <div className="py-16 text-center">
              <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-eye-line text-2xl text-foreground-400"></i>
              </span>
              <p className="text-foreground-700 font-medium mb-2">未找到相关作品</p>
              <p className="text-sm text-foreground-500 mb-8">换个关键词试试，或浏览热门分类</p>
              <div className="flex flex-wrap justify-center gap-3">
                {hotCategories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    className="px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                  >
                    <i className={`${cat.icon}`}></i>
                    {cat.name}（{cat.count}）
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {paged.map((work, i) => (
                  <WorkCard key={work.id} work={work} index={i} />
                ))}
              </div>
              <Pagination
                current={safePage}
                total={results.length}
                pageSize={PAGE_SIZE}
                onChange={(p) => {
                  setPage(p);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            </>
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}